import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { AvatarPreferences } from '@/types/avatar'
import type { PlayerProfile } from '@/types/profile'
import { normalizeMembershipTier } from '@/types/profile'
import { defaultAvatarPreferences } from '@/lib/avatar-options'

const API_BASE = '/api/v2/euchre'
const LAST_PROFILE_STORAGE_KEY = 'euchre:last-profile'

function mergePreferences(preferences?: Partial<AvatarPreferences> | null): AvatarPreferences {
  return {
    ...defaultAvatarPreferences(),
    ...(preferences ?? {}),
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeProfile(raw: any): PlayerProfile {
  return {
    email: raw.email,
    name: raw.name,
    picture: raw.picture,
    avatarPreferences: mergePreferences(raw.avatarPreferences),
    gamesPlayed: raw.gamesPlayed ?? 0,
    gamesWon: raw.gamesWon ?? 0,
    totalScore: raw.totalScore ?? 0,
    createdAt: raw.createdAt ?? '',
    lastActiveAt: raw.lastActiveAt ?? '',
    preferredTeamId: raw.preferredTeamId ?? null,
    membershipTier: normalizeMembershipTier(raw.membershipTier),
  }
}

function cloneProfile(profile: PlayerProfile): PlayerProfile {
  return {
    ...profile,
    avatarPreferences: { ...profile.avatarPreferences },
  }
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch (error) {
    console.warn('Local storage is not available.', error)
    return null
  }
}

function readCachedProfile(): PlayerProfile | null {
  const storage = getStorage()
  if (!storage) return null

  try {
    const raw = storage.getItem(LAST_PROFILE_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return normalizeProfile(parsed)
  } catch (error) {
    console.warn('Failed to read cached profile. Clearing stored value.', error)
    storage.removeItem(LAST_PROFILE_STORAGE_KEY)
    return null
  }
}

function writeCachedProfile(profile: PlayerProfile | null) {
  const storage = getStorage()
  if (!storage) return

  if (!profile) {
    storage.removeItem(LAST_PROFILE_STORAGE_KEY)
    return
  }

  try {
    storage.setItem(LAST_PROFILE_STORAGE_KEY, JSON.stringify(profile))
  } catch (error) {
    console.warn('Failed to write cached profile.', error)
  }
}

export const useProfileStore = defineStore('profile', () => {
  const cachedProfile = ref<PlayerProfile | null>(readCachedProfile())
  const profile = ref<PlayerProfile | null>(
    cachedProfile.value ? cloneProfile(cachedProfile.value) : null
  )
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isLoaded = ref(cachedProfile.value !== null)

  const profileSnapshot = computed(() => profile.value ?? cachedProfile.value)
  const hasProfile = computed(() => profileSnapshot.value !== null)

  function persistProfile(next: PlayerProfile | null) {
    if (next) {
      const normalized = normalizeProfile(next)
      profile.value = cloneProfile(normalized)
      cachedProfile.value = normalized
      writeCachedProfile(normalized)
    } else {
      profile.value = null
      cachedProfile.value = null
      writeCachedProfile(null)
    }
    isLoaded.value = true
  }

  async function fetchProfile() {
    try {
      isLoading.value = true
      error.value = null

      const response = await fetch(`${API_BASE}/user`)
      if (response.status === 404) {
        persistProfile(null)
        return null
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.status}`)
      }

      const data = await response.json()
      const normalized = normalizeProfile(data)
      persistProfile(normalized)
      return profile.value
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    } finally {
      isLoading.value = false
      if (!isLoaded.value) {
        isLoaded.value = true
      }
    }
  }

  async function saveProfile(payload: { name: string; avatarPreferences: AvatarPreferences }) {
    try {
      isLoading.value = true
      error.value = null

      const response = await fetch(`${API_BASE}/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`Failed to save profile: ${response.status}`)
      }

      const data = await response.json()
      const normalized = normalizeProfile(data)
      persistProfile(normalized)
      return profile.value
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    } finally {
      isLoading.value = false
      if (!isLoaded.value) {
        isLoaded.value = true
      }
    }
  }

  function setProfile(data: PlayerProfile | null) {
    persistProfile(data)
  }

  function clearCachedProfile() {
    persistProfile(null)
  }

  return {
    profile,
    profileSnapshot,
    isLoading,
    error,
    isLoaded,
    hasProfile,
    fetchProfile,
    saveProfile,
    setProfile,
    clearCachedProfile,
  }
})
